import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    clientName: '',
    eventName: '',
    eventDate: '',
    status: 'active',
    data: '{}'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch all offers
  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/offers`);
      const data = await response.json();
      setOffers(data.offers || []);
    } catch (err) {
      setError('Chyba při načítání nabídek');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  // Open modal for creating new offer
  const handleNewOffer = () => {
    setEditingOffer(null);
    setFormData({
      slug: '',
      clientName: '',
      eventName: '',
      eventDate: '',
      status: 'active',
      data: '{}'
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  // Open modal for editing existing offer
  const handleEditOffer = (offer) => {
    setEditingOffer(offer);
    
    // Extract all fields except basic ones into data object
    const { slug, clientName, eventName, eventDate, status, viewCount, createdAt, updatedAt, ...restData } = offer;
    
    setFormData({
      slug: offer.slug,
      clientName: offer.clientName,
      eventName: offer.eventName,
      eventDate: offer.eventDate,
      status: offer.status || 'active',
      data: JSON.stringify(restData, null, 2)
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  // Save offer (create or update)
  const handleSaveOffer = async () => {
    try {
      setError('');
      setSuccess('');

      // Validate JSON
      let parsedData;
      try {
        parsedData = JSON.parse(formData.data);
      } catch (e) {
        setError('Neplatný JSON formát v poli Data');
        return;
      }

      const payload = {
        slug: formData.slug,
        clientName: formData.clientName,
        eventName: formData.eventName,
        eventDate: formData.eventDate,
        status: formData.status,
        data: parsedData
      };

      let response;
      if (editingOffer) {
        // Update existing
        response = await fetch(`${API_URL}/api/admin/offers/${editingOffer.slug}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        // Create new
        response = await fetch(`${API_URL}/api/admin/offers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Chyba při ukládání nabídky');
      }

      setSuccess(editingOffer ? 'Nabídka aktualizována' : 'Nabídka vytvořena');
      setShowModal(false);
      fetchOffers();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete offer
  const handleDeleteOffer = async (slug) => {
    if (!window.confirm(`Opravdu chcete smazat nabídku "${slug}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/offers/${slug}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Chyba při mazání nabídky');
      }

      setSuccess('Nabídka smazána');
      fetchOffers();
    } catch (err) {
      setError(err.message);
    }
  };

  // Duplicate offer
  const handleDuplicateOffer = async (slug) => {
    const newSlug = prompt('Zadejte nový slug pro duplikát:', `${slug}-copy`);
    if (!newSlug) return;

    const newClient = prompt('Zadejte název nového klienta:', '');
    if (!newClient) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/offers/${slug}/duplicate?new_slug=${encodeURIComponent(newSlug)}&new_client=${encodeURIComponent(newClient)}`, {
        method: 'POST'
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Chyba při duplikování nabídky');
      }

      setSuccess(`Nabídka duplikována jako "${newSlug}"`);
      fetchOffers();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Správa nabídek</h1>
          <p className="text-gray-600">Přehled a editace všech nabídek</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
            {success}
          </div>
        )}

        {/* Actions */}
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            onClick={handleNewOffer}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            + Nová nabídka
          </button>
          <a
            href="/admin/berani-orders"
            data-testid="link-berani-orders"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            🐏 Objednávky Berani Skybox →
          </a>
          <a
            href="/admin/berani-requests"
            data-testid="link-berani-requests"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            📋 Požadavky Berani gastro →
          </a>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-600">Načítání...</div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slug
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Klient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Akce
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zobrazení
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Akce
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {offers.map((offer) => (
                  <tr key={offer.slug} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {offer.slug}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {offer.clientName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {offer.eventName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {offer.eventDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        offer.status === 'active' ? 'bg-green-100 text-green-800' :
                        offer.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {offer.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {offer.viewCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditOffer(offer)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Editovat
                      </button>
                      <button
                        onClick={() => handleDuplicateOffer(offer.slug)}
                        className="text-green-600 hover:text-green-900 mr-3"
                      >
                        Duplikovat
                      </button>
                      <button
                        onClick={() => handleDeleteOffer(offer.slug)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Smazat
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {offers.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                Zatím nejsou žádné nabídky
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingOffer ? 'Editovat nabídku' : 'Nová nabídka'}
              </h2>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug (URL adresa)
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="napr. wanzl, fatra, dako"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Klient
                  </label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Název klienta"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Název akce
                  </label>
                  <input
                    type="text"
                    value={formData.eventName}
                    onChange={(e) => setFormData({...formData, eventName: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="např. Family Day 2026"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Datum akce
                  </label>
                  <input
                    type="text"
                    value={formData.eventDate}
                    onChange={(e) => setFormData({...formData, eventDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="např. 15. června 2026"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data (JSON)
                  </label>
                  <textarea
                    value={formData.data}
                    onChange={(e) => setFormData({...formData, data: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                    rows="12"
                    placeholder='{"key": "value"}'
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Komplexní data nabídky ve formátu JSON
                  </p>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Zrušit
                </button>
                <button
                  onClick={handleSaveOffer}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  {editingOffer ? 'Uložit změny' : 'Vytvořit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
